import { FC, useEffect, useRef, useState } from "react";
import StepInfo from "../components/StepInfo";
import { Content } from "./Content/Content";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import { Button } from "components/shared";
import { BUTTON_VARIANTS } from "components/shared/Button/Button";
import Search from "components-new/Search";
import {
	ChangeBatchesMode,
	ChangeFormationMode,
	ChangeSearchValue,
	ClearAttachmentList,
	ClearGlobalChecked,
	ClearPaginationData,
	resetStep_6,
} from "toolkit/steps/stepSix/stepSix.slice";
import { Legend } from "./Legend/Legend";
import { Batches } from "./Batches/Batches";
import { getBatches, getSearchedBatchObjects, getSearchedData } from "toolkit/steps/stepSix/stepSix.thunks";
import { useLocation, useParams } from "react-router-dom";
import { ModalWindow } from "components/ModalWindow/ModalWindow";
import { FormationDocument } from "./FormationDocument/FormationDocument";
import { ViewSwitch } from "./ViewSwitch/ViewSwitch";
import ConfirmNavigate from "components/ConfirmNavigate";
import useDebounce from "hooks/useDebounce";
import { PAGINATION_COUNT_STEP_SIX } from "constant";
import styles from "./StepSix.module.sass";
import cn from "classnames";

export const StepSix: FC = () => {
	const selectedOrganization = useAppSelector((state) => state.organizations.selectedOrganization);
	const dispatch = useAppDispatch();
	const { data, searchValue, formationMode, batchesMode, batchList, currentBatch, attachmentList } = useAppSelector(
		(state) => state.stepSix,
	);
	const location = useLocation();
	const [confirmAction, setConfirmAction] = useState<boolean>(false);
	const { organizationId } = useParams();
	const [modalWindow, setModalWindow] = useState<boolean>(false);
	const [batchesAnim, setBatchesAnim] = useState<boolean>(false);

	useEffect(() => {
		dispatch(getBatches(Number(organizationId)));
		//eslint-disable-next-line
	}, []);

	const query = useDebounce(searchValue, 500);
	useEffect(() => {
		if (query as string) {
			if (!batchesMode)
				dispatch(
					getSearchedData({
						organizationId: Number(organizationId),
						searchValue: String(query),
						formationMode,
						part: 1,
						count: PAGINATION_COUNT_STEP_SIX,
					}),
				);
			else
				dispatch(
					getSearchedBatchObjects({
						organizationId: Number(organizationId),
						batchId: Number(currentBatch?.id),
						searchValue: String(query),
						part: 1,
						count: PAGINATION_COUNT_STEP_SIX,
					}),
				);
		}
		//eslint-disable-next-line
	}, [query]);

	useEffect(() => {
		if (location.state?.outline) {
			batchesModeHandler(true);
		}
		//eslint-disable-next-line
	}, [location.state]);

	useEffect(() => {
		return () => {
			dispatch(resetStep_6());
		};
		//eslint-disable-next-line
	}, []);

	const formationModeHandler = (value: boolean) => {
		if (batchesMode) {
			setModalWindow(true);
			return;
		}
		dispatch(ChangeFormationMode(value));
		dispatch(ChangeSearchValue(""));
		if (!value) {
			dispatch(ClearAttachmentList());
			dispatch(ClearGlobalChecked());
		}
	};

	const timeoutRef: any = useRef();
	const batchesModeHandler = (value: boolean) => {
		clearTimeout(timeoutRef.current);
		dispatch(ChangeSearchValue(""));
		if (value) {
			dispatch(ChangeBatchesMode(value));
			dispatch(ClearPaginationData({ clearBatches: false }));
			setBatchesAnim(true);
		} else {
			setBatchesAnim(false);
			timeoutRef.current = window.setTimeout(() => {
				dispatch(ClearPaginationData({ clearBatches: false }));
				dispatch(ChangeBatchesMode(value));
			}, 300);
		}
	};

	const handleSearch = (text: string) => {
		dispatch(ChangeSearchValue(text));
	};

	const checkSelection = () => {
		if (data.repeatable.some((object) => attachmentList.includes(object.id))) {
			setConfirmAction(true);
		} else {
			setConfirmAction(false);
			setModalWindow(true);
		}
	};

	const confirmClick = () => {
		setModalWindow(true);
		setConfirmAction(false);
	};

	return (
		<main className={styles.content}>
			<section className={styles.categorization}>
				{modalWindow && (
					<ModalWindow close={() => setModalWindow(false)}>
						<FormationDocument close={() => setModalWindow(false)} />
					</ModalWindow>
				)}
				<ConfirmNavigate toggle={confirmAction} close={() => setConfirmAction(false)} clickHandler={confirmClick} />
				<div className={styles.header}>
					<StepInfo
						stepTitle={"6. Подготовка и отправка результирующих документов"}
						info="Подготовка пакета итоговых документов по всей организации для выгрузки и отправки в ФСТЭК"
						companyName={selectedOrganization?.fullName}
					/>
					<div className={styles["header-item"]}>
						{batchesAnim && (
							<button className={styles["txt-button"]} onClick={() => batchesModeHandler(false)}>
								Скрыть партии пакетов
							</button>
						)}
						<Search
							className={styles.search}
							searchValue={searchValue}
							handleSearch={(e) => handleSearch(e.target.value)}
						/>
						{formationMode ? (
							<>
								<Button
									onClick={() => formationModeHandler(false)}
									className={styles.button}
									variant={BUTTON_VARIANTS.OUTLINED}>
									Отмена
								</Button>
								<Button
									onClick={checkSelection}
									className={cn(styles.button, styles.pending)}
									disabled={!attachmentList.length}>
									Готово
								</Button>
							</>
						) : (
							<>
								{!batchesAnim && (
									<Button
										onClick={() => batchesModeHandler(true)}
										className={styles.button}
										variant={BUTTON_VARIANTS.OUTLINED}
										disabled={Boolean(!batchList.length)}>
										Показать партии пакетов
									</Button>
								)}
								<Button
									onClick={() => formationModeHandler(true)}
									className={styles.button}
									disabled={
										(!formationMode &&
											!batchesMode &&
											!data.categorized.length &&
											!data.repeatable.some((object) => object.approvalDate && object.timeToSend)) ||
										(batchesMode && currentBatch?.id === 0)
									}>
									Сформировать пакет документов
								</Button>
							</>
						)}
						<ViewSwitch />
					</div>
				</div>
				<div className={styles["batches-wrp"]}>
					<div className={cn(styles.anim, batchesAnim && styles.animgo)}>{batchesMode && <Batches />}</div>
					<div data-comment-false={true} className={styles["content-wrp"]}>
						<Legend />
						<Content />
					</div>
				</div>
			</section>
		</main>
	);
};
